<template>
  <form class="m-4 md:mx-8 md:mt-16">
    <section>
      <h1 class="px-4 md:px-8 py-2 text-2xl font-bold bg-primario text-gray-200 tracking-wide rounded-t">
        {{ $t("lang.msg.comercialRequest") }}
      </h1>
      <div class="bg-white p-4 md:p-8">
        <p class="text-primario text-xl" v-if="comercialRequest.idComercialRequest">
          <span>{{ concatTwoValues(comercialRequest.comercialRequestReference, comercialRequest.comercialRequestReferenceNumber) }}</span>
        </p>

        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-4"
          :class="{'mt-8': comercialRequest.idComercialRequest}"
        >
          <div class="space-y-4">
            <InputWrapper :label="$t('lang.msg.reference2')" content="input">
                <InputText v-model="comercialRequest.comercialRequestReference2" :name="$t('lang.msg.reference2')" />
            </InputWrapper>
            <InputWrapper :label="$t('lang.msg.internalSalesman')" content="input">
              <Select
                id="idEmployee"
                :data="employees"
                track-by="employeeName"
                track-by2="employeeLastName"
                v-model.number="comercialRequest.idEmployee"
                @change="changeEmployee($event)"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.signupDate')" content="input">
              <v-date-picker
                v-model="comercialRequest.comercialRequestActivationDate"
                mode="single"
                title-position="left"
                :max-date="new Date()"
                :input-props="{
                  class: 'datepicker hover:border-blue-500',
                  placeholder: $t('lang.msg.signupDate'),
                }"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.collection')" content="input">
              <Select
                id="idCollection"
                :data="collections"
                :track-by="getLangLabel('collection')"
                v-model.number="comercialRequest.idCollection"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.season')" content="input">
              <Select
                id="idSeason"
                :data="seasons"
                :track-by="getLangLabel('season')"
                v-model.number="comercialRequest.idSeason"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.subfamilyArticle')" content="input">
              <Select
                id="idSubfamilyArticle"
                :data="subfamilyArticles"
                :track-by="getLangLabel('subfamilyArticle')"
                v-model.number="comercialRequest.idSubfamilyArticle"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.client')" content="input" required>
              <Select
                id="idClient"
                ref="idClient"
                :data="clients"
                track-by="clientName"
                :error="formHasErrors && !comercialRequest.idClient"
                v-model.number="comercialRequest.idClient"
                @change="filterPerClient($event)"
              />
            </InputWrapper>
          </div>

          <div class="space-y-4">
            <InputWrapper :label="$t('lang.msg.department')" content="input">
              <Select
                id="idSection"
                :data="sections"
                track-by="sectionName"
                v-model.number="comercialRequest.idSection"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.buyer')" content="input">
              <Select
                id="idBuyer"
                :data="filteredBuyers"
                track-by="buyerName"
                track-by2="buyerLastName"
                v-model.number="comercialRequest.idBuyer"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.clientReference')" content="input">
              <InputText v-model="comercialRequest.comercialRequestClientReference" :name="$t('lang.msg.clientReference')" />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.clientDesigner')" content="input">
              <Select
                id="idDesigner"
                :data="filteredDesigners"
                track-by="designerName"
                track-by2="designerLastName"
                v-model.number="comercialRequest.idClientDesigner"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.comercialNecessityDate')" content="input">
              <v-date-picker
                v-model="comercialRequest.comercialRequestNeededDate"
                mode="single"
                title-position="left"
                :input-props="{
                  class: 'datepicker hover:border-blue-500',
                  placeholder: $t('lang.msg.comercialNecessityDate'),
                }"
              />
            </InputWrapper>

            <InputWrapper :label="$t('lang.msg.designNecessityDate')" content="input">
              <v-date-picker
                v-model="comercialRequest.comercialRequestDesignDelivery"
                mode="single"
                title-position="left"
                :input-props="{
                  class: 'datepicker hover:border-blue-500',
                  placeholder: $t('lang.msg.designNecessityDate'),
                }"
              />
            </InputWrapper>
          </div>
        </div>

        <!-- === RADIOBUTTONS === -->
        <div class="mt-8 flex">
          <label class="inline-flex items-center">
            <input
              v-model="comercialRequest.internalDevelopment"
              type="radio"
              class="form-radio"
              :value="true"
            >
            <span class="ml-2 cursor-pointer">{{ $t("lang.msg.clientRequest") }}</span>
          </label>
          <label class="inline-flex items-center ml-6">
            <input
              v-model="comercialRequest.internalDevelopment"
              type="radio"
              class="form-radio"
              :value="false"
            >
            <span class="ml-2 cursor-pointer">{{ $t("lang.msg.internalDevelopment") }}</span>
          </label>
        </div>
      </div>
    </section>

    <!-- =================== -->
    <!-- === DESCRIPCIÓN === -->
    <!-- =================== -->
    <section class="bg-white mb-8">
      <div class="px-4 md:px-8 text-lg font-semibold bg-secundario text-primario tracking-wide">
        <h2>{{ $t("lang.msg.description") }}</h2>
      </div>
      <div class="flex flex-1 p-4 md:p-8">
        <textarea
          v-model="comercialRequest.comercialRequestConcept"
          class="form-textarea w-full bg-white leading-7 tracking-wide text-gray-800"
          :placeholder="$t('lang.msg.concept')"
        />
      </div>
    </section>

    <!-- =================== -->
    <!-- === COMENTARIOS === -->
    <!-- =================== -->
    <section v-if="hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])">
      <div class="px-4 md:px-8 text-lg font-semibold bg-secundario text-primario tracking-wide rounded-t">
        <h2>{{ $t("lang.msg.comments") }}</h2>
      </div>
      <Comments
        class="p-4 md:p-8"
        :comments="comercialRequest.comen"
        @comment-added="commentAdded"
        :id-comercial-request="idComercialRequest"
      />
    </section>

    <!-- ===================== -->
    <!-- === ASIGNAR TAREA === -->
    <!-- ===================== -->
    <section v-if="idComercialRequest != undefined && hasRoles(['ADMIN', 'DIRECCIÓN', 'EQUIPO EUROPROSEM'])">
      <div class="px-4 md:px-8 text-lg font-semibold bg-secundario text-primario tracking-wide">
        <h2>{{ $t("lang.msg.assignTask") }}</h2>
      </div>
      <TaskAssign
        :id-origen-tarea="comercialRequest.idComercialRequest"
        origen-tarea="comercialRequest"
        :referencia="referencia"
      />
    </section>

    <!-- ================ -->
    <!-- === IMÁGENES === -->
    <!-- ================ -->
    <section>
      <div class="px-4 md:px-8 text-lg font-semibold bg-secundario text-primario tracking-wide">
        <h2>{{ $t("lang.msg.referencePhotos") }}</h2>
      </div>
      <vue-dropzone
        ref="dropzone"
        id="dropzone"
        :options="dropzoneOptions"
        @vdropzone-queue-complete="leaveComercialRequest"
        @vdropzone-sending="sendingEvent"
      />

      <div class="gallery">
        <ImageGallery
          v-for="(image, imageIndex) in comercialRequest.images"
          :key="`image_${imageIndex}`"
          :url="image.ruta"
          :id-foto="image.idFoto"
          @image-deleted="imageDeleted"
        />
      </div>
    </section>

    <!-- ============ -->
    <!-- === TAGS === -->
    <!-- ============ -->
    <Tags
      @change="onTagChange"
      v-model="comercialRequest.tags"
      :id-comercial-request="idComercialRequest"
    />

    <section class="mt-8">
      <div class="bg-white p-4 md:p-8 space-y-2 rounded-sm">
        <div class="flex flex-col lg:flex-row lg:items-center items-start justify-between">
          <div class="flex items-center my-2 lg:my-0 text-sm">
            <ToggleButton :checked="comercialRequest.comercialToDesign" @change="toggleComercialToDesignStep">
              {{ $t("lang.msg.stepToDesignDepartment") }}
            </ToggleButton>
          </div>
          <div class="w-full md:w-auto my-2 md:my-0 space-y-4 md:space-y-0 space-x-0 md:space-x-4">
            <input
              v-model="comercialRequest.comercialToDesignDate"
              class="form-input w-full md:w-56 h-8"
              type="text"
              disabled
              name="comercialToDesignDate"
            >
            <input
              v-model="comercialRequest.comercialToDesignUserName"
              class="form-input w-full md:w-56 h-8"
              type="text"
              disabled
            >
          </div>
        </div>
        <div class="flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 space-x-0 md:space-x-4">
          <div class="w-full md:w-56">
            <ButtonAction @action="cloneComercialRequest" large>
              {{ $t("lang.msg.cloneComercialRequest") }}
            </ButtonAction>
          </div>
          <div class="w-full md:w-56">
            <ButtonAction @action="() => isDiscontinueModalOpen = true" large>
              {{ $t("lang.msg.deleteComercialRequest") }}
            </ButtonAction>
          </div>
        </div>
        <div class="w-full flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 space-x-0 md:space-x-4">
          <div class="w-full md:w-56">
            <ButtonAction
                        @action="confirmStepToProduction"
                          large
                          >
                <span style="color:white;display:flex;flex-direction:row;align-items: center;padding: 0.5em">
                <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    width="30"
                     height="28"
                     viewBox="0 0 30 28"
                fill="white">
                    <path d="M14 14c0-2.203-1.797-4-4-4s-4 1.797-4 4 1.797 4 4 4 4-1.797 4-4zM26 22c0-1.094-0.906-2-2-2s-2 0.906-2 2c0 1.109 0.906 2 2 2 1.109 0 2-0.906 2-2zM26 6c0-1.094-0.906-2-2-2s-2 0.906-2 2c0 1.109 0.906 2 2 2 1.109 0 2-0.906 2-2zM20 12.578v2.891c0 0.203-0.156 0.438-0.359 0.469l-2.422 0.375c-0.125 0.406-0.297 0.797-0.5 1.188 0.438 0.625 0.906 1.203 1.406 1.797 0.063 0.094 0.109 0.187 0.109 0.313 0 0.109-0.031 0.219-0.109 0.297-0.313 0.422-2.063 2.328-2.516 2.328-0.125 0-0.234-0.047-0.328-0.109l-1.797-1.406c-0.391 0.203-0.781 0.359-1.203 0.484-0.078 0.797-0.156 1.656-0.359 2.422-0.063 0.219-0.25 0.375-0.469 0.375h-2.906c-0.219 0-0.438-0.172-0.469-0.391l-0.359-2.391c-0.406-0.125-0.797-0.297-1.172-0.484l-1.844 1.391c-0.078 0.078-0.203 0.109-0.313 0.109-0.125 0-0.234-0.047-0.328-0.125-0.406-0.375-2.25-2.047-2.25-2.5 0-0.109 0.047-0.203 0.109-0.297 0.453-0.594 0.922-1.172 1.375-1.781-0.219-0.422-0.406-0.844-0.547-1.281l-2.375-0.375c-0.219-0.031-0.375-0.234-0.375-0.453v-2.891c0-0.203 0.156-0.438 0.359-0.469l2.422-0.375c0.125-0.406 0.297-0.797 0.5-1.188-0.438-0.625-0.906-1.203-1.406-1.797-0.063-0.094-0.109-0.203-0.109-0.313s0.031-0.219 0.109-0.313c0.313-0.422 2.063-2.312 2.516-2.312 0.125 0 0.234 0.047 0.328 0.109l1.797 1.406c0.391-0.203 0.781-0.359 1.203-0.5 0.078-0.781 0.156-1.641 0.359-2.406 0.063-0.219 0.25-0.375 0.469-0.375h2.906c0.219 0 0.438 0.172 0.469 0.391l0.359 2.391c0.406 0.125 0.797 0.297 1.172 0.484l1.844-1.391c0.094-0.078 0.203-0.109 0.313-0.109 0.125 0 0.234 0.047 0.328 0.125 0.406 0.375 2.25 2.063 2.25 2.5 0 0.109-0.047 0.203-0.109 0.297-0.453 0.609-0.922 1.172-1.359 1.781 0.203 0.422 0.391 0.844 0.531 1.281l2.375 0.359c0.219 0.047 0.375 0.25 0.375 0.469zM30 20.906v2.188c0 0.234-2.016 0.453-2.328 0.484-0.125 0.297-0.281 0.562-0.469 0.812 0.141 0.313 0.797 1.875 0.797 2.156 0 0.047-0.016 0.078-0.063 0.109-0.187 0.109-1.859 1.109-1.937 1.109-0.203 0-1.375-1.563-1.531-1.797-0.156 0.016-0.313 0.031-0.469 0.031s-0.313-0.016-0.469-0.031c-0.156 0.234-1.328 1.797-1.531 1.797-0.078 0-1.75-1-1.937-1.109-0.047-0.031-0.063-0.078-0.063-0.109 0-0.266 0.656-1.844 0.797-2.156-0.187-0.25-0.344-0.516-0.469-0.812-0.313-0.031-2.328-0.25-2.328-0.484v-2.188c0-0.234 2.016-0.453 2.328-0.484 0.125-0.281 0.281-0.562 0.469-0.812-0.141-0.313-0.797-1.891-0.797-2.156 0-0.031 0.016-0.078 0.063-0.109 0.187-0.094 1.859-1.094 1.937-1.094 0.203 0 1.375 1.547 1.531 1.781 0.156-0.016 0.313-0.031 0.469-0.031s0.313 0.016 0.469 0.031c0.438-0.609 0.906-1.219 1.437-1.75l0.094-0.031c0.078 0 1.75 0.984 1.937 1.094 0.047 0.031 0.063 0.078 0.063 0.109 0 0.281-0.656 1.844-0.797 2.156 0.187 0.25 0.344 0.531 0.469 0.812 0.313 0.031 2.328 0.25 2.328 0.484zM30 4.906v2.187c0 0.234-2.016 0.453-2.328 0.484-0.125 0.297-0.281 0.562-0.469 0.812 0.141 0.313 0.797 1.875 0.797 2.156 0 0.047-0.016 0.078-0.063 0.109-0.187 0.109-1.859 1.109-1.937 1.109-0.203 0-1.375-1.563-1.531-1.797-0.156 0.016-0.313 0.031-0.469 0.031s-0.313-0.016-0.469-0.031c-0.156 0.234-1.328 1.797-1.531 1.797-0.078 0-1.75-1-1.937-1.109-0.047-0.031-0.063-0.078-0.063-0.109 0-0.266 0.656-1.844 0.797-2.156-0.187-0.25-0.344-0.516-0.469-0.812-0.313-0.031-2.328-0.25-2.328-0.484v-2.188c0-0.234 2.016-0.453 2.328-0.484 0.125-0.281 0.281-0.562 0.469-0.812-0.141-0.313-0.797-1.891-0.797-2.156 0-0.031 0.016-0.078 0.063-0.109 0.187-0.094 1.859-1.094 1.937-1.094 0.203 0 1.375 1.547 1.531 1.781 0.156-0.016 0.313-0.031 0.469-0.031s0.313 0.016 0.469 0.031c0.438-0.609 0.906-1.219 1.437-1.75l0.094-0.031c0.078 0 1.75 0.984 1.937 1.094 0.047 0.031 0.063 0.078 0.063 0.109 0 0.281-0.656 1.844-0.797 2.156 0.187 0.25 0.344 0.531 0.469 0.812 0.313 0.031 2.328 0.25 2.328 0.484z"></path>
                </svg>
                    <span class="md:ml-1">
              {{ $t("lang.msg.stepToProduction") }}
                    </span>
                </span>
            </ButtonAction>
          </div>
          <div class="w-full md:w-56">
            <ButtonAction @action="confirmSave" large>
            <span style="color:white;display:flex;flex-direction:row;align-items: center;justify-content:center;padding: 0.5em">
                <svg
                    version="1.1"
                     xmlns="http://www.w3.org/2000/svg"
                     width="24"
                     height="28"
                    fill="white"
                     viewBox="0 0 24 28">
                    <path d="M6 24h12v-6h-12v6zM20 24h2v-14c0-0.297-0.266-0.938-0.469-1.141l-4.391-4.391c-0.219-0.219-0.828-0.469-1.141-0.469v6.5c0 0.828-0.672 1.5-1.5 1.5h-9c-0.828 0-1.5-0.672-1.5-1.5v-6.5h-2v20h2v-6.5c0-0.828 0.672-1.5 1.5-1.5h13c0.828 0 1.5 0.672 1.5 1.5v6.5zM14 9.5v-5c0-0.266-0.234-0.5-0.5-0.5h-3c-0.266 0-0.5 0.234-0.5 0.5v5c0 0.266 0.234 0.5 0.5 0.5h3c0.266 0 0.5-0.234 0.5-0.5zM24 10v14.5c0 0.828-0.672 1.5-1.5 1.5h-21c-0.828 0-1.5-0.672-1.5-1.5v-21c0-0.828 0.672-1.5 1.5-1.5h14.5c0.828 0 1.969 0.469 2.562 1.062l4.375 4.375c0.594 0.594 1.062 1.734 1.062 2.562z"></path>
                </svg>
                <span class="md:ml-1">
              {{ $t("lang.msg.save") }}
                </span>
                </span>
            </ButtonAction>
          </div>
        </div>
      </div>
    </section>
    <TheModal
        :open="isNextStepModalOpen"
        :title="$t('lang.msg.nextStep')"
        @close="isNextStepModalOpen=false"
    >
        <template #body>
            <Confirmation :to="'Producción'"/>
        </template>
        <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
              type="button"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 hover:bg-green-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
          @click="stepToProduction">Aceptar</button>
        </span>
        </template>
    </TheModal>
    <TheModal
        :open="confirmSaveModal"
        :title="$t('lang.msg.save')"
        @close="confirmSaveModal = false"
    >
        <template #body>
            ¿Guardar la muestra comercial?
        </template>
        <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
              type="button"
              class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 hover:bg-green-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
          @click="createOrUpdateComercialRequest">Aceptar</button>
        </span>
        </template>
    </TheModal>
    <TheModal
      :open="isDiscontinueModalOpen"
      :title="$t('lang.msg.discontinue')"
      @close="isDiscontinueModalOpen = false"
    >
      <template #body>
        <p class="mt-4 pr-16 text-gray-700 font-semibold">
          {{ $t('lang.msg.discontinueComercialRequest') }}
        </p>
        <textarea
          rows="2"
          class="form-textarea w-full mt-2"
          placeholder="Escriba el motivo aquí"
          v-model="cancellationReason"
        />
      </template>
      <template #action>
        <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
          <button
            type="button"
            class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-red-600 hover:bg-red-700 text-base font-medium text-white shadow-sm focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150"
            @click="discontinueComercialRequest"
          >{{ $t('lang.msg.discontinue') }}</button>
        </span>
      </template>
    </TheModal>
  </form>
</template>

<script>
// MIXINS
import UtilsMixin from "@/components/mixins/UtilsMixin";
import CalendarMixin from "@/components/mixins/CalendarMixin";

// COMPONENTS
import Tags from "@/components/shared/sections/Tags";
import Comments from "@/components/Comments";
import TaskAssign from "@/components/TaskAssign";

// DROPZONE
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

// MODAL
import TheModal from "@/components/shared/TheModal";
import Confirmation from "@/components/modals/Confirmation";

export default {
  name: "NewComercialRequest",
  components: {
      Confirmation,
    Tags,
    Comments,
    TaskAssign,
    vueDropzone: vue2Dropzone,
    TheModal
  },
  mixins: [UtilsMixin, CalendarMixin],
  data() {
    return {
      comercialRequest: {
        comercialRequestReference2: null,
        comercialRequestActivationDate: null,
        comercialRequestDesignDelivery: null,
        comercialRequestNeededDate: null,
        sampleTaskSampleResolutionDate: "2021-01-01T00:00:00.000Z",
        productionTaskProductionEndDate: "2021-01-01T00:00:00.000Z",
        comercialRequestClientReference: null,
        comercialToDesign: false,
        comercialToDesignDate: null,
        comercialToDesignUserName: "",
        idEmployee: null,
        idCompany: null,
        idCollection: null,
        idSeason: null,
        idSubfamilyArticle: null,
        idClient: null,
        idSection: null,
        idBuyer: null,
        idClientDesigner: null,
        internalDevelopment: true,
        tags: null,
        comen: [],
        images: [],
        completado: false,
        idUser: 0,
        designToSampleDate: "2021-01-01T00:00:00.000Z"
      },
      // MODAL
      isDiscontinueModalOpen: false,
      isNextStepModalOpen: false,
      cancellationReason: "",
      // GENERAL DATA
      employees: [],
      collections: [],
      seasons: [],
      subfamilyArticles: [],
      clients: [],
      sections: [],
      buyers: [],
      designers: [],
      // FILTERS
      filteredBuyers: [],
      filteredDesigners: [],
      // DROPZONE OPTIONS
      dropzoneOptions: {
        timeout: 120000,
        maxFilesize: 2048,
        url: `${this.$API_URL}/api/FileUpload/FotoReferenciaFichaUpload`,
        thumbnailWidth: 175,
        thumbnailHeight: 175,
        autoProcessQueue: this.$route.params.idComercialRequest ? true : false,
        paramName: "files"
      },
      // ROUTE PARAMS
      idComercialRequest: 0,
      idDesign: 0,
      // VALIDATIONS
      formHasErrors: false,
      confirmSaveModal:false
    };
  },
  computed: {
    referencia() {
      return (
        this.comercialRequest.comercialRequestReference + '' +
        this.comercialRequest.comercialRequestReferenceNumber
      );
    }
  },
  created() {
    window.scrollTo(0, 0);
    this.idComercialRequest = this.$route.params.idComercialRequest;
    this.fetchData();
  },
  methods: {
    /**
     *  The function will load first every data needed, and then if the idComercialRequest parameter is passed,
     *  It will make an axios call.
     */
    fetchData() {
      let langParams = {
        lang: this.language
      };

      Promise.all([
        this.$axios.post(`api/Employees/GetBy`, {
            rolName: "COMERCIAL",
            lang: this.language
        }),
        this.$axios.get(`api/Buyers/GetAllBuyer`),
        this.$axios.get(`api/Clients/GetAllClient`),
        this.$axios.get(`api/Designers/GetDesigner`),
        this.$axios.post(`api/Collections/GetCollectionActive`, langParams),
        this.$axios.post(`api/Seasons/GetSeason`, langParams),
        this.$axios.post(`api/SubfamilyArticles/getSubfamilyArticle`, langParams),
        this.$axios.get(`api/Sections/getSection`)
      ])
        .then((values) => {
          this.employees = this.getActive(values[0].data, 'active');
          this.buyers = this.getActive(values[1].data, 'active');
          this.clients = this.getActive(values[2].data, 'active');
          this.designers = this.getActive(values[3].data, 'active');
          this.collections = this.getActive(values[4].data, 'collectionAction');
          this.seasons = this.getActive(values[5].data, 'seasonActive');
          this.subfamilyArticles = this.getActive(values[6].data, 'subfamilyArticleActive');
          this.sections = this.getActive(values[7].data, 'active');
        })
        .then(() => {
          // Get the ComercialRequest after everything is loaded.
          if (this.idComercialRequest !== undefined) {
            this.idComercialRequest = parseInt(this.idComercialRequest);
            this.fetchComercialRequestValues();
          }
        });
    },
    /**
     *  Fetch comercialRequest Tags, Comments, Images...
     */
    fetchComercialRequestValues() {
      let params = {
        idComercialRequest: this.idComercialRequest
      };

      Promise.all([
        this.$axios.post(`api/ComercialRequests/GetThisComerRequest`, params),
        this.$axios.post(`api/Comments/GetAllComment`, params),
        this.$axios.post(`api/Tags/GetAllTag`, params),
        this.$axios.post(`api/Fotoes/GetAllFoto`, params)
      ]).then((values) => {
        this.comercialRequest = values[0].data[0];
        this.comercialRequest.comen = values[1].data || [];
        this.comercialRequest.tags = values[2].data || [];
        this.comercialRequest.images = values[3].data || [];

        // Applies filters from the comercialRequest.
        if (this.comercialRequest.idClient != null) {
          this.filterPerClient(this.comercialRequest.idClient);
        }

        // Format the dates we got from the server.
        this.formatDateToString(this.comercialRequest, [
          "comercialToDesignDate"
        ]);
        this.formatDatetimesToDates(this.comercialRequest, [
          "comercialRequestNeededDate",
          "comercialRequestActivationDate",
          "comercialRequestDesignDelivery",
          "sampleTaskSampleResolutionDate"
        ]);
      });
    },
    confirmSave(){
      this.confirmSaveModal = true;
    },
    createOrUpdateComercialRequest() {
        this.confirmSaveModal = false;
      if (!this.comercialRequest.idClient) {
        this.formHasErrors = true;

        // Scroll to Input with errors.
        this.$nextTick(() => {
          window.scrollTo(0, this.$refs.idClient.$el.offsetTop - 300);
        });
      } else {
        // Clone the object to avoid formatting back and forth.
        let paramsClone = { ...this.comercialRequest };

        // Format date before sending to the server
        this.formatStringsToDates(paramsClone, [
          "comercialToDesignDate"
        ]);
        this.formatDatesToDatetimes(paramsClone, [
          "comercialRequestNeededDate",
          "comercialRequestActivationDate",
          "comercialRequestDesignDelivery",
          "sampleTaskSampleResolutionDate"
        ]);

        paramsClone.idBaseColor = null;
        paramsClone.idFactory = null;
        paramsClone.idSize = null;
        paramsClone.diseñador = null;
        paramsClone.completado = this.checkIfComercialRequestIsCompleted();
        this.$set(paramsClone, "idUser", this.user.id);

        // Url y parámetros dinámicos según el rol.
        let url = "";
        let message = "";

        // La URL cambia si el ID de la Solicitud existe o no.
        if (this.comercialRequest.idComercialRequest != null) {
          url = "api/ComercialRequests/Update";
          message = "Solicitud comercial actualizada";
        } else {
          url = "api/ComercialRequests/Add";
          message = "Solicitud comercial creada";
        }

        // Save or Update the comercial request.
        this.$axios
          .post(url, paramsClone)
          .then((response) => {
            if (response.data) {
              // Add the latest idComercialRequest.
              this.$set(this.comercialRequest, "idComercialRequest", response.data);
            }
            this.openAlert("success", message, null);

            // If dropzone has to process some files
            if (this.$refs.dropzone.getQueuedFiles().length != 0) {
              this.$refs.dropzone.processQueue();
            }
          })
          .catch(() => this.showFriendlyError());
      }
    },
    leaveComercialRequest() {
      if (!this.$route.params.idComercialRequest) {
        this.$router.push({ name: "planningComercialRequest" });
      }
    },
    /**
     *  Sets the employee and modifies the idCompany variable.
     */
    changeEmployee(idEmployee) {
      if (idEmployee) {
        let employee = this.employees.find((el) => {
          return el.idEmployee == idEmployee;
        });

        this.$set(this.comercialRequest, "idCompany", employee.idCompany);
      }
    },
    /**
     *  Filters the 'Buyers' and 'Designers' when the 'Client' is selected.
     */
    filterPerClient(idClient) {
      if (idClient) {
        this.filteredBuyers = this.buyers.filter((el) => {
          return el.idClient == idClient;
        });

        this.filteredDesigners = this.designers.filter((el) => {
          return el.idClient == idClient;
        });
      }
    },
      confirmStepToProduction(){
          this.isNextStepModalOpen = true;
      },
    /**
     *  Paso a production con valores añadidos.
     */
    stepToProduction() {
      // Format date before sending to the server
      this.formatStringsToDates(this.comercialRequest, [
        "comercialToDesignDate"
      ]);
      this.formatDatesToDatetimes(this.comercialRequest, [
        "comercialRequestNeededDate",
        "comercialRequestActivationDate",
        "comercialRequestDesignDelivery",
        "fechaEstimadaLlegada",
        "fechaLlegadaReal",
        "fechaEnvioFabrica"
      ]);

      // Modificamos algunos valores necesarios para pasar a producción.
      this.$set(this.comercialRequest, "completado", this.checkIfComercialRequestIsCompleted());

      // Pasamos estas 2 fechas como nulas.
      this.$set(this.comercialRequest, "sampleTaskSampleResolutionDate", "2021-01-01T00:00:00.000Z");
      this.$set(this.comercialRequest, "productionTaskProductionEndDate", "2021-01-01T00:00:00.000Z");

      // Añadimos el idUser del usuario actual.
      this.$set(this.comercialRequest, "idUser", this.user.id);

      this.$axios
        .post(
          "api/ComercialRequests/ComercialToProduction",
          this.comercialRequest
        )
        .then(() => {
          this.openAlert("success", "Paso de solicitud a producción", null);
          this.$router.push({ name: "planningProduction" });
        });
    },
    /**
     *  Toggle button to continue to the next step (Design).
     */
    toggleComercialToDesignStep(value) {
      if (value) {
        this.$set(this.comercialRequest, "comercialToDesignDate", this.getCurrentDateAndHour());
        this.$set(this.comercialRequest, "comercialToDesignUser", this.user.id);
        this.$set(this.comercialRequest, "comercialToDesignUserName", this.user.fullname);
      } else {
        // If it returns false, then we empty their values
        this.$set(this.comercialRequest, "comercialToDesignDate", "");
        this.$set(this.comercialRequest, "comercialToDesignUser", null);
        this.$set(this.comercialRequest, "comercialToDesignUserName", null);
      }

      this.$set(this.comercialRequest, "comercialToDesign", value);
    },
    /**
     *  Helper function to check if the comercial request if completed.
     */
    checkIfComercialRequestIsCompleted() {
      return (
        this.comercialRequest.idEmployee != null &&
        this.comercialRequest.idCollection != null &&
        this.comercialRequest.idSeason != null &&
        this.comercialRequest.idSubfamilyArticle != null &&
        this.comercialRequest.idClient != null &&
        this.comercialRequest.idSection != null &&
        this.comercialRequest.idBuyer != null &&
        this.comercialRequest.idClientDesigner != null &&
        this.comercialRequest.comercialRequestActivationDate != null &&
        this.comercialRequest.comercialRequestNeededDate != null &&
        this.comercialRequest.comercialRequestDesignDelivery != null
      );
    },
    cloneComercialRequest() {
      this.$axios
        .post("api/Clonations/ClonationComercialRequest", {
          idComercialRequest: parseInt(this.idComercialRequest),
          idUser: this.user.id,
          comercialRequestDesignDelivery: "2021-01-01T00:00:00.000Z",
          comercialRequestNeededDate: "2021-01-01T00:00:00.000Z",
          comercialToDesignDate: "2021-01-01T00:00:00.000Z",
          designToSampleDate: "2021-01-01T00:00:00.000Z"
        })
        .then(() => {
          this.$router.push({ name: "planningComercialRequest" });
        });
    },
    discontinueComercialRequest() {
      this.$axios
        .post("api/ComercialRequests/Descatalogo", {
          idUser: this.user.id,
          idComercialRequest: this.idComercialRequest,
          comercialRequestCancellationReason: this.cancellationReason
        })
        .then(() => {
          this.openAlert("success", "Solicitud comercial descatalogada", null);
          this.isDiscontinueModalOpen = false;
          this.cancellationReason = "";
        })
        .catch(() => this.showFriendlyError());
    },
    commentAdded(item) {
      if (this.idComercialRequest == null) {
        delete item.idComercialRequest;
      }
      this.comercialRequest.comen.unshift(item);
    },
    onTagChange(array) {
      this.$set(this.comercialRequest, "tags", array);
    },
    sendingEvent(file, xhr, formData) {
      formData.append("referencia", true);
      formData.append("idComercialRequest", this.comercialRequest.idComercialRequest);
    },
    imageDeleted(idFoto) {
      let images = this.comercialRequest.images.filter(image => {
        return image.idFoto != idFoto
      });

      this.$set(this.comercialRequest, 'images', images);
      this.$forceUpdate();
    }
  }
};
</script>
